import React, { useEffect, useState } from 'react';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { errorMessage, responseTexts, permissions } from '@helpers/Constants';
import { Module, AddRoleModalProps } from '@helpers/Models';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { useInputValidation } from '@hooks/useInputValidation';
import { AxiosError } from 'axios';

export const AddRoleModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToastMessage } = useToast();

  const nameValidation = useInputValidation('textOnly', '');
  const [modules, setModules] = useState<Module[]>([]);
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<
    { id: number; permissions: Record<string, boolean> }[]
  >([]);

  const resetForm = () => {
    nameValidation.reset();
    setDescription('');
    setSelectedPermissions([]);
  };

  useEffect(() => {
    if (!isOpen) {
      setModules([]);
      resetForm();
    } else {
      fetchModules();
    }
  }, [isOpen]);

  /**
   * Fetches the list of modules from the server and initializes the state with the fetched modules.
   *
   * This function performs the following steps:
   * 1. Sends a POST request to fetch the modules.
   * 2. Updates the state with the fetched modules.
   * 3. Initializes the permissions for each module with default values (read, write, edit, delete set to false).
   * 4. Sets the initialized permissions in the state.
   *
   * If an error occurs during the fetch operation, a toast message is displayed with an error message.
   *
   * @async
   * @function fetchModules
   * @returns {Promise<void>} A promise that resolves when the fetch operation is complete.
   */
  const fetchModules = async (): Promise<void> => {
    try {
      const moduleResponse = await Axios.post(ApiEndpoints.FETCH_MODULES, {});
      const fetchedModules: Module[] = moduleResponse.data.data;

      setModules(fetchedModules);

      // Initialize permissions as an array of objects
      const permissionsState = fetchedModules.map((module) => ({
        id: Number(module.id), // Ensure ID is a number
        permissions: permissions.reduce(
          (acc, perm) => {
            acc[perm] = false; // Set each permission to false by default
            return acc;
          },
          {} as Record<string, boolean>,
        ), // permissions is now an object
      }));

      setSelectedPermissions(permissionsState); // Set as an array
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_MODULES, 'error');
    }
  };

  /**
   * Handles the change event for a checkbox, toggling the specified permission
   * for the given module ID in the selected permissions state.
   *
   * @param moduleId - The ID of the module for which the permission is being toggled.
   * @param permission - The specific permission to be toggled.
   */
  const handleCheckboxChange = (moduleId: number, permission: string): void => {
    setSelectedPermissions((prev) =>
      prev.map((module) =>
        module.id === moduleId
          ? {
              ...module,
              permissions: {
                ...module.permissions,
                [permission]: !module.permissions[permission], // Toggle the permission value
              },
            }
          : module,
      ),
    );
  };

  /**
   * Handles the form submission for creating a new role.
   *
   * This function formats the selected permissions into the required payload structure
   * and sends a POST request to create a new role. It also handles form reset and
   * displays appropriate toast messages based on the success or failure of the request.
   *
   * @param {React.FormEvent} event - The form submission event.
   *
   * @returns {Promise<void>} A promise that resolves when the role creation process is complete.
   *
   * @throws Will display an error toast message if the role creation fails.
   */
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    // Validate name input
    if (!nameValidation.isValid || !nameValidation.value.trim()) {
      showToastMessage(
        'Please enter a valid role name with only letters and spaces.',
        'error',
      );
      return;
    }

    //loop through selectedPermission
    selectedPermissions.forEach((module) => {
      //if module.permissions.all is true, then add all permissions('read', 'write', 'edit', 'delete') to the module
      if (module.permissions.all) {
        module.permissions = {
          read: true,
          write: true,
          edit: true,
          delete: true,
        };
      } else {
        module.permissions = {
          read: false,
          write: false,
          edit: false,
          delete: false,
        };
      }
    });

    //send only selected Persmissions if read, write, edit and delete is true. and remove the module if everything is false
    const filteredPermissions = selectedPermissions
      .map((module) => ({
        id: module.id,
        permissions: Object.fromEntries(
          Object.entries(module.permissions).filter(([_, value]) => value),
        ),
      }))
      .filter((module) => Object.keys(module.permissions).length > 0);

    const payload = {
      id: 0, // 0 is not required for creating a new role
      name: nameValidation.value.trim(),
      description,
      modules: filteredPermissions,
    };

    try {
      await Axios.post(ApiEndpoints.UPSERT_ROLE, payload);
      resetForm();
      showToastMessage(responseTexts.ROLE_CREATED, 'success');
      onClose();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        showToastMessage(error.response.data.errors[0].message, 'error');
      } else {
        showToastMessage(errorMessage.ERROR_CREATING_ROLE, 'error');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-6">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-bold">Add New Role</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Role Title Input */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Role Title *
            </label>
            <input
              type="text"
              value={nameValidation.value}
              onChange={nameValidation.handleChange}
              onKeyPress={nameValidation.handleKeyPress}
              placeholder="Store Manager"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              required
            />
            {nameValidation.error && (
              <p className="text-red-500 text-xs mt-1">
                {nameValidation.error}
              </p>
            )}
          </div>

          {/* Description Input */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes or description..."
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              rows={3}
            />
          </div>

          {/* Modules and Permissions Table */}
          <div className="mt-6">
            <div className="grid grid-cols-2 font-medium border-b pb-2">
              <span>Modules</span>
              <span className="text-center">Permissions</span>
            </div>

            {modules.map((module) => (
              <div key={module.id} className="grid grid-cols-2 py-2 border-b">
                <span>{module.name}</span>
                <div className="flex gap-4 justify-center">
                  {permissions.map((perm) => (
                    <label key={perm} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={
                          selectedPermissions.find(
                            (p) => p.id === Number(module.id),
                          )?.permissions[perm] || false // Find the module by ID
                        } // Access the permission directly
                        onChange={() =>
                          handleCheckboxChange(Number(module.id), perm)
                        } // Pass module ID and permission name
                        className="w-4 h-4 rounded accent-primary"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Modal Footer Buttons */}
          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-6 py-2 h-10 rounded-md text-white ${
                Object.values(selectedPermissions).some((module) =>
                  Object.values(module.permissions).some((perm) => perm),
                )
                  ? 'bg-primary'
                  : 'bg-gray-400'
              }`}
              disabled={
                !Object.values(selectedPermissions).some((module) =>
                  Object.values(module.permissions).some((perm) => perm),
                )
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoleModal;
